import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { FiCheck } from "react-icons/fi";
import { MdErrorOutline } from "react-icons/md";
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { dateFormats, getTasaApproval, kaplanCpdType, kaplanMapping, TaskStatus } from '../../../../constants';
import { adminActions } from '../../../../state/modules/admin';
import { pluralise } from '../../../../utils';
import { ConfirmationModal, FileUploadFormField, GeneralTable, InfoBanner, LoadingButton } from '../../../components';
import Loading from '../../loading';

const uuidv1 = require('uuid/v1');
const csv = require('csvtojson')

const kaplanReportField = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email Address',
  activityId: 'Activity ID',
  date: 'Date of Activity',
  name: 'Name of Activity',
  provider: 'Activity Conducted By'
}

const KaplanUpload = ({
  csvFile,
  setCsvFile,
  setParsed,
  parsed,
  handleUpload,
  allCpdCategories,
  isUploading,
  uploadSuccess,
  showConfirmation,
  toggleConfirmation,
  isFetchingUsers,
  usersForUpload,
  confirmationUserPoints,
  confirmUserNames,
  initUploadTasks,
  progress,
  setParsingFile,
  isParsingFile
}) => {
  const onCsvDrop = (acceptedFiles, rejectedFiles) => {
    setParsingFile(true)
    const acceptedFile = fp.first(acceptedFiles);
    const rejectedFile = fp.first(rejectedFiles);

    if (acceptedFile) {
      var reader = new FileReader();
      reader.onload = () => {
        const fileContent = reader.result
        csv()
          .fromString(fileContent)
          .then((csvJson) => {
            const parsed = fp.map(
              (row) => {
                const allCpdPoints = fp.flow(
                  fp.keys,
                  fp.map(
                    (key) => {
                      const kaplanName = key
                      const caddieKey = kaplanMapping[key]
                      const points = row[key]
                      const cpdCategory = fp.find(
                        ({ name }) => name === fp.get('name')(caddieKey)
                      )(allCpdCategories)
                      const cpdCategoryId = fp.get('id')(cpdCategory)
                      return {
                        type: fp.get('type')(caddieKey),
                        points: points && parseFloat(points, 10),
                        cpdCategoryId: cpdCategoryId && parseInt(cpdCategoryId, 10),
                        cpdCategory,
                        kaplanName
                      }
                    }
                  )
                )(kaplanMapping)
                const cpdPointsToImport = fp.flow(
                  fp.filter(
                    ({ cpdCategoryId, points, type }) => cpdCategoryId && points && (type === kaplanCpdType.import)
                  ),
                  fp.groupBy('cpdCategoryId'),
                  fp.toPairs,
                  fp.map(
                    ([cpdCategoryId, cpdRows]) => ({
                      cpdCategoryId: parseInt(cpdCategoryId, 10),
                      cpdCategoryName: fp.get('[0].cpdCategory.name')(cpdRows),
                      points: fp.sumBy('points')(cpdRows),
                      validated: false
                    })
                  )
                )(allCpdPoints)
                const cpdPointsToDocument = fp.flow(
                  fp.filter(
                    ({ cpdCategoryId, points, type }) => cpdCategoryId && points && (type === kaplanCpdType.document)
                  ),
                  fp.map(
                    ({ kaplanName, points }) => `${points} ${pluralise('point', points)} in ${kaplanName}`
                  ),
                )(allCpdPoints)
                const description = fp.isEmpty(cpdPointsToDocument) ? '' : `Points imported includes, ${cpdPointsToDocument.join(',')}.`
                const dateString = row[kaplanReportField.date]
                const provider = row[kaplanReportField.provider]
                const dateDivided = dateString.split('/')
                const day = parseInt(dateDivided[0], 10)
                const month = parseInt(dateDivided[1], 10) - 1
                const rawYear = parseInt(dateDivided[2], 10)
                const year = rawYear > 2000 ? rawYear : rawYear + 2000
                const date = new Date(year, month, day)
                const tpbApproved = getTasaApproval(row)
                return {
                  identifier: uuidv1(),
                  firstName: row[kaplanReportField.firstName],
                  lastName: row[kaplanReportField.lastName],
                  id: row[kaplanReportField.activityId],
                  name: row[kaplanReportField.name],
                  email: row[kaplanReportField.email],
                  date,
                  cpdPoints: cpdPointsToImport,
                  description,
                  provider,
                  tpbApproved
                }
              }
            )(csvJson)
            const filtered = fp.filter(
              ({ cpdPoints }) => cpdPoints.length > 0
            )(parsed)
            setParsed(filtered)
            setParsingFile(false)
          })
      }
      reader.readAsText(acceptedFile);
      setCsvFile(acceptedFile, undefined)
    }

    if (rejectedFile) {
      return setCsvFile(undefined, 'This file cannot be read')
    }
  }

  const columns = [
    {
      key: 'taskStatus',
      name: `Upload${progress}`
    },
    {
      key: 'user',
      name: 'User'
    },
    {
      key: 'activity',
      name: 'Activity'
    },
    {
      key: 'tpb',
      name: 'TPB'
    },
    {
      key: 'description',
      name: 'Details'
    }
  ]
  const rows = fp.map(
    ({ taskStatus, provider, firstName, lastName, id, name, email, date, cpdPoints, description, tpbApproved }) => {
      const cpdPointRows = fp.map(
        ({ cpdCategoryId, cpdCategoryName, points }) => {
          return <li key={cpdCategoryId}>{`${cpdCategoryName} - ${points}`}</li>
        }
      )(cpdPoints)
      const totalPoints = fp.sumBy('points')(cpdPoints)
      const getTaskStatusIcon = () => {
        if (taskStatus === TaskStatus.updating) {
          return <Spinner name='circle' className='spinner' fadeIn='none' />
        }
        if (taskStatus === TaskStatus.completed) {
          return <FiCheck />
        }
        if (taskStatus === TaskStatus.failed) {
          return <MdErrorOutline />
        }
      }
      return {
        taskStatus: {
          value: getTaskStatusIcon()
        },
        user: {
          value: <div>
            <div><strong>Name:</strong>{` ${firstName} ${lastName}`}</div>
            <div><strong>Email:</strong>{` ${email}`}</div>
          </div>
        },
        activity: {
          value: <div>
            <div><strong>Name:</strong>{` ${name}`}</div>
            <div><strong>Provider:</strong>{` ${provider}`}</div>
            <div><strong>Accreditation Number:</strong>{` ${id}`}</div>
            <div><strong>Total Points:</strong>{` ${totalPoints}`}</div>
            <div><strong>Date:</strong>{` ${dateFnsCompat.format(date, dateFormats.ShortDate)}`}</div>
          </div>
        },
        tpb: {
          value: tpbApproved ? 'Yes' : 'No',
          className: 'tpb'
        },
        description: {
          value: <div>
            <h3>CPD point break down</h3>
            <ul>{cpdPointRows}</ul>

            <h3>Description</h3>
            <div>{description}</div>
          </div>,
          className: 'notes'
        }
      }
    }
  )(parsed)

  const uploadConfirmationMessage = isFetchingUsers ?
    <Loading /> :
    (usersForUpload.length === 0) ?
      <div>Failed to find a caddie user for your upload</div> :
      <div>
        <p>You are about to upload:</p>
        <div>
          {
            fp.map(
              ({ fullName, email, externalCpds }) => <p key={email}>{`${externalCpds.length} external CPD ${pluralise('record', externalCpds.length)} to ${fullName} (${email})`}</p>
            )(confirmationUserPoints)
          }
        </div>
        <div>
          {
            confirmUserNames.length > 0 ?
              <h3 className='warning-text'>Warning</h3> :
              null
          }
          <div>
            {
              fp.map(
                (message) => <p key={message} className='warning-text'>{message}</p>
              )(confirmUserNames)
            }
          </div>
        </div>
      </div>

  const isDataValidated = fp.flow(
    fp.map(
      ({ cpdPoints }) => {
        const cpdPointsValidated = fp.flow(
          fp.all(
            ({ validated }) => validated
          )
        )(cpdPoints)
        return cpdPointsValidated
      }
    ),
    fp.all(
      (validated) => validated
    )
  )(parsed)

  return <div id='kaplan-upload'>
    <h2>Kaplan Upload</h2>
    <form>
      <Row className='no-gutter'>
        <FileUploadFormField
          styleName='kaplan-file'
          accept='.csv'
          multiple={false}
          onFileDrop={onCsvDrop}
          file={csvFile}
          dropLabel='Upload CSV file' />
      </Row>
      {
        uploadSuccess ?
          <Row className='no-gutter'>
            <Col>
              <InfoBanner local={true} success={true} info='CPD Points have been uploaded successfully' />
            </Col>
          </Row> :
          <Row className='no-gutter'>
            {
              parsed ?
                <Col>
                  <LoadingButton key={showConfirmation} loading={isUploading} text='Upload' onClick={toggleConfirmation} />
                </Col> :
                null
            }
            <Col>
              <h3>CPD points to upload</h3>
            </Col>
            <Col>
              {
                (isFetchingUsers || isParsingFile || !isDataValidated) ?
                  <Loading /> :
                  <GeneralTable className='kaplan-upload-table' columns={columns} rows={rows} />
              }
            </Col>
            <ConfirmationModal
              id="confirm-kaplan-upload"
              show={showConfirmation}
              actions={[
                {
                  onConfirmAction: () => {
                    toggleConfirmation();
                    initUploadTasks();
                  },
                  actionLabel: 'Yes',
                  hideActionButton: usersForUpload.length === 0
                }
              ]}
              cancel={() => toggleConfirmation()}
              message={uploadConfirmationMessage}
              headingText={<span>Upload Kaplan CPD Points</span>}
            />
          </Row>
      }
    </form>
  </div>
}

const mapStateToProps = ({ cpdCategorys, admin, industry }) => ({
  allCpdCategories: cpdCategorys.cpdCategorys,
  isUploading: admin.isUploadingCPD,
  usersForUpload: admin.usersForKaplanUpload,
  isFetchingUsers: admin.isFetchingUser,
  uploadResult: admin.externalCpdUploadResult,
  industries: industry.industriesWithCpdCategorys
})
const mapDispatchToProps = (dispatch) => ({
  upload: bindActionCreators(adminActions.admin.uploadExternalCpd, dispatch),
  getUsersFromEmails: bindActionCreators(adminActions.admin.getUsersFromEmails, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    {
      parsed: undefined,
      csvFile: undefined,
      validation: undefined,
      uploadSuccess: false,
      showConfirmation: false,
      isParsingFile: false
    },
    {
      setParsingFile: () => (isParsingFile) => ({
        isParsingFile
      }),
      setCsvFile: () => (csvFile, validation) => ({
        csvFile,
        validation,
        uploadSuccess: false
      }),
      setParsed: () => (parsed) => ({
        parsed
      }),
      onUploadSuccess: () => () => ({
        csvFile: undefined,
        validation: undefined,
        uploadSuccess: true,
        parsed: undefined
      }),
      toggleConfirmation: ({ showConfirmation }) => () => ({
        showConfirmation: !showConfirmation
      }),
      initUploadTasks: ({ parsed }) => () => ({
        parsed: fp.map(
          (entry) => ({
            ...entry,
            taskStatus: TaskStatus.initiated
          })
        )(parsed)
      }),
      onTaskStarted: ({ parsed }) => ({ identifier }) => ({
        parsed: fp.map(
          (entry) => entry.identifier === identifier ? {
            ...entry,
            taskStatus: TaskStatus.updating
          } : entry
        )(parsed)
      }),
      onTaskFinished: ({ parsed }) => (success) => ({
        parsed: fp.map(
          (entry) => entry.taskStatus === TaskStatus.updating ? {
            ...entry,
            taskStatus: success ? TaskStatus.completed : TaskStatus.failed
          } : entry
        )(parsed)
      }),
      refreshCpdCategorysForUsers: ({ parsed }, { industries }) => (userProfiles) => ({
        parsed: fp.map(
          (entry) => {
            const updatedPoints = fp.map(
              (cpdCategoryPoint) => {
                const user = fp.find(
                  ({ user }) => fp.lowerCase(user.email) === fp.lowerCase(entry.email)
                )(userProfiles)
                const userIndustryCpdCategorys = fp.flow(
                  fp.get('trainingPlans'),
                  fp.map(
                    ({ industry }) => {
                      const industryCpdCategorys = fp.flow(
                        fp.find(
                          (industryWithCpdCategory) => industryWithCpdCategory.industry.id === industry.id
                        ),
                        fp.get('cpdCategorys'),
                        fp.map(
                          (cpdCategory) => ({
                            ...cpdCategory,
                            name: he.decode(cpdCategory.name)
                          })
                        )
                      )(industries)
                      return industryCpdCategorys
                    }
                  ),
                  fp.flatten,
                )(user)
                const cpdCategoryId = fp.flow(
                  fp.find(
                    ({ name }) => name === cpdCategoryPoint.cpdCategoryName
                  ),
                  fp.get('id')
                )(userIndustryCpdCategorys)

                return {
                  ...cpdCategoryPoint,
                  cpdCategoryId,
                  validated: true
                }
              }
            )(entry.cpdPoints)
            return {
              ...entry,
              cpdPoints: updatedPoints
            }
          }
        )(parsed)
      })
    }
  ),
  withProps(
    ({ parsed, usersForUpload }) => {
      const userEmails = fp.flow(
        fp.map('email'),
        fp.uniq
      )(parsed)

      const confirmationUserPoints = fp.map(
        ({ user: { email, firstName, lastName } }) => {
          const externalCpds = fp.flow(
            fp.filter(
              (v) => fp.lowerCase(v.email) === fp.lowerCase(email)
            )
          )(parsed)
          return {
            fullName: `${firstName} ${lastName}`,
            email,
            externalCpds
          }
        }
      )(usersForUpload)

      const confirmUserNames = fp.flow(
        fp.flatMap(
          ({ user: { email, firstName, lastName } }) => {
            const uploaded = fp.filter((v) => fp.lowerCase(v.email) === fp.lowerCase(email))(parsed)
            const fullName = `${firstName} ${lastName}`
            const inconsistentFullNames = fp.flow(
              fp.map(
                (v) => `${v.firstName} ${v.lastName}`
              ),
              fp.uniq,
              fp.filter(
                (n) => n !== fullName
              )
            )(uploaded)
            return fp.map(
              (wrongName) => `Inconsistency detected! User (${email})'s name is ${fullName} instead of ${wrongName}`
            )(inconsistentFullNames)
          }
        )
      )(usersForUpload)

      const nextUploadTask = fp.find(
        ({ taskStatus }) => taskStatus === TaskStatus.initiated
      )(parsed)

      const uploadingTask = fp.find(
        ({ taskStatus }) => taskStatus === TaskStatus.updating
      )(parsed)

      const completedCount = fp.flow(
        fp.filter(
          ({ taskStatus }) => taskStatus === TaskStatus.completed
        ),
        fp.get('length')
      )(parsed)
      const failedCount = fp.flow(
        fp.filter(
          ({ taskStatus }) => taskStatus === TaskStatus.failed
        ),
        fp.get('length')
      )(parsed)
      const totalCount = fp.get('length')(parsed)
      const progress = totalCount ? `(${completedCount}/${failedCount}/${totalCount})` : ''

      return {
        confirmationUserPoints,
        confirmUserNames,
        userEmails,
        nextUploadTask,
        uploadingTask,
        progress
      }
    }
  ),
  withHandlers({
    handleUpload: ({ upload, onTaskStarted }) => (nextUploadTask) => {
      const externalCpds = fp.map(
        ({ id, name, email, date, cpdPoints, description, provider, tpbApproved }) => {
          const externalCpd = {
            userEmail: email,
            timeEarned: date,
            activityName: name,
            accreditationNumber: id,
            description,
            provider,
            tpbApproved
          }
          const cpdCategoryPoints = fp.map(
            ({ cpdCategoryId, points }) => ({ cpdCategoryId, points })
          )(cpdPoints)
          return {
            externalCpd,
            cpdCategoryPoints,
            documentedPoints: []
          }
        }
      )([nextUploadTask])
      const data = {
        externalCpds
      }
      onTaskStarted(nextUploadTask)
      upload(data)
    }
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        isUploading,
        userEmails,
        getUsersFromEmails,
        nextUploadTask,
        uploadingTask,
        handleUpload,
        onTaskFinished,
        uploadResult,
        usersForUpload,
        refreshCpdCategorysForUsers
      } = this.props
      if (!isUploading && prevProps.isUploading) {
        onTaskFinished(!fp.isEmpty(uploadResult))
      }

      if (!fp.equals(userEmails)(prevProps.userEmails)) {
        getUsersFromEmails(userEmails)
      }

      if (!uploadingTask && nextUploadTask) {
        handleUpload(nextUploadTask)
      }

      if (!fp.equals(usersForUpload)(prevProps.usersForUpload)) {
        refreshCpdCategorysForUsers(usersForUpload)
      }
    }
  })
)(KaplanUpload)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/cpd-point-portal/kaplan-upload.js